import React, { useState, useEffect } from 'react';
import './PendingApprovals.css';
import { getBackendUrl } from '../../../api';
import Navbar1 from '../Navbar/Navbar1.jsx';
import Mainbar from '../Navbar/Mainbar.jsx';
import { colors } from '@mui/material';

const Approvals = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('All Types');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [comment, setComment] = useState('');
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [approvalsData, setApprovalsData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  
  const [isHistoryModalOpen, setIsHistoryModalOpen] = useState(false);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [employeeHistory, setEmployeeHistory] = useState(null);

  const [userProfilePictures, setUserProfilePictures] = useState({});

  const [managerInfo, setManagerInfo] = useState({
    managerId: null,
    teamSize: 0,
    canApproveLeaves: false
  });

  const formatDateForDisplay = (dateStr) => {
    if (!dateStr) return '';
    
    try {
      let date;
      if (dateStr.includes('/')) {
        return dateStr;
      } else if (dateStr.includes('-')) {
        date = new Date(dateStr);
      } else {
        return dateStr;
      }
      
      if (isNaN(date.getTime())) {
        return dateStr;
      }
      
      return date.toLocaleDateString('en-GB');
    } catch (error) {
      console.error('Error formatting date:', error);
      return dateStr;
    }
  };
  // ✅ Default placeholder image - you can replace this with your own default avatar
  const DEFAULT_PROFILE_PICTURE = '/Images/default-avatar.png'; // or use a data URL or any default image path

  const formatLeaveType = (leaveType) => {
    if (!leaveType) return 'Unknown';
    
    const typeMap = {
      'casual': 'Casual Leave',
      'sick': 'Medical Leave', 
      'medical': 'Medical Leave',
      'emergency': 'Permission Leave',
      'permission': 'Permission Leave',
      'annual': 'Annual Leave',
      'maternity': 'Maternity Leave',
      'paternity': 'Paternity Leave',
      'unpaid': 'Unpaid Leave'
    };
    
    return typeMap[leaveType.toLowerCase()] || leaveType;
  };

  const determinePriority = (request) => {
    const startDate = new Date(request.startDateISO || request.startDate || request.start_date);
    const today = new Date();
    const daysUntilStart = Math.ceil((startDate - today) / (1000 * 60 * 60 * 24));
    
    if (daysUntilStart <= 1) return 'high';
    if (daysUntilStart <= 3) return 'medium';
    return 'low';
  };

  // ✅ ENHANCED: Get user profile picture with better logging
const getUserProfilePicture = async (employeeEmail, employeeName) => {
  // ✅ Default to Manureva logo (or change to any default image you want)
  const DEFAULT_PROFILE_PICTURE = '/Images/Manurevalogo.png';
  
  console.log('🖼️ [Admin] Getting profile picture for:', { employeeEmail, employeeName });
  
  // Create cache key
  const cacheKey = employeeEmail || employeeName;
  
  // Return from cache if already fetched
  if (userProfilePictures[cacheKey]) {
    console.log('📦 [Admin] Using cached profile picture');
    return userProfilePictures[cacheKey];
  }
  
  // If no identifier provided, return default
  if (!employeeEmail && !employeeName) {
    console.log('⚠️ [Admin] No employee identifier provided, using default');
    return DEFAULT_PROFILE_PICTURE;
  }
  
  try {
    const backendUrl = getBackendUrl();
    console.log('🌐 [Admin] Fetching employee list from:', `${backendUrl}/api/employee-list`);
    
    const response = await fetch(`${backendUrl}/api/employee-list`, {
      method: 'GET',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
      },
    });
    
    if (response.ok) {
      const employees = await response.json();
      console.log(`✅ [Admin] Fetched ${employees.length} employees`);
      
      // Find the employee by email or name
      const employee = employees.find(emp => 
        emp.email === employeeEmail || 
        emp.name === employeeName
      );
      
      if (employee) {
        console.log('👤 [Admin] Found employee:', employee.name);
        
        // Check if employee has a profile picture set
        if (employee.profile_picture && employee.profile_picture.trim() !== '') {
          console.log('✅ [Admin] Employee has custom profile picture:', employee.profile_picture);
          
          // Cache and return the custom profile picture
          setUserProfilePictures(prev => ({
            ...prev,
            [cacheKey]: employee.profile_picture
          }));
          
          return employee.profile_picture;
        } else {
          console.log('ℹ️ [Admin] Employee has no profile picture set, using default Manureva logo');
        }
      } else {
        console.log('⚠️ [Admin] Employee not found in list');
      }
    } else {
      console.error('❌ [Admin] Failed to fetch employee list:', response.status);
    }
  } catch (error) {
    console.error('❌ [Admin] Error fetching employee list:', error);
  }
  
  // Cache and return default picture (Manureva logo)
  console.log('📸 [Admin] Using default Manureva logo');
  setUserProfilePictures(prev => ({
    ...prev,
    [cacheKey]: DEFAULT_PROFILE_PICTURE
  }));
  
  return DEFAULT_PROFILE_PICTURE;
};
  const getMedicalDocumentInfo = (request) => {
    console.log('Checking document info for request:', request);
    
    let hasDocument = false;
    let documentPath = null;
    let fileName = 'Medical Certificate';
    
    const documentIndicators = [
      request.has_document === true,
      request.certificate === true,
      request.has_medical_document === true,
      
      request.document_filename && 
      typeof request.document_filename === 'string' && 
      request.document_filename.trim() !== '' && 
      request.document_filename !== 'null',
      
      request.certificateFileName && 
      typeof request.certificateFileName === 'string' && 
      request.certificateFileName.trim() !== '' && 
      request.certificateFileName !== 'null',
      
      request.medical_document_filename && 
      typeof request.medical_document_filename === 'string' && 
      request.medical_document_filename.trim() !== '',
      
      request.document_path && 
      typeof request.document_path === 'string' && 
      request.document_path.trim() !== '' && 
      request.document_path !== 'null',
      
      request.medical_document_path && 
      typeof request.medical_document_path === 'string' && 
      request.medical_document_path.trim() !== '',
      
      request.certificatePath && 
      typeof request.certificatePath === 'string' && 
      request.certificatePath.trim() !== '',
      
      request.file_path && 
      typeof request.file_path === 'string' && 
      request.file_path.trim() !== '',
      
      request.certificateUrl && 
      typeof request.certificateUrl === 'string' && 
      request.certificateUrl.trim() !== '',
      
      request.document_url && 
      typeof request.document_url === 'string' && 
      request.document_url.trim() !== ''
    ];
    
    const positiveIndicators = documentIndicators.filter(Boolean).length;
    hasDocument = positiveIndicators > 0;
    
    if (hasDocument) {
      const possibleFileNames = [
        request.document_filename,
        request.certificateFileName,
        request.medical_document_filename,
        request.original_filename
      ].filter(name => name && typeof name === 'string' && name.trim() !== '' && name !== 'null');
      
      fileName = possibleFileNames[0] || 'Medical Certificate';
      
      const possiblePaths = [
        request.certificateUrl,
        request.document_url
      ].filter(path => path && typeof path === 'string' && path.trim() !== '');
      
      documentPath = possiblePaths[0] || `/api/download-medical-document/${request.id}`;
      
      console.log('Document found:', {
        hasDocument: true,
        fileName: fileName,
        documentPath: documentPath,
        indicators_triggered: positiveIndicators
      });
      
      return {
        hasDocument: true,
        downloadUrl: documentPath,
        fileName: fileName
      };
    }
    
    const isMedicalLeave = (request.leaveType || request.leave_type || '').toLowerCase().includes('medical') ||
                          (request.leaveType || request.leave_type || '').toLowerCase().includes('sick');
    
    console.log(isMedicalLeave ? 'Medical leave without document detected' : 'No document found');
    
    return {
      hasDocument: false,
      isMedicalLeave: isMedicalLeave
    };
  };

  const handleDocumentDownload = async (downloadUrl, fileName, requestData) => {
    try {
      console.log('Attempting to download document:', { downloadUrl, fileName, requestId: requestData?.id });
      
      const response = await fetch(downloadUrl, {
        method: 'GET',
        credentials: 'include',
        headers: {
          'Accept': 'application/pdf,application/octet-stream,*/*',
        },
      });
      
      console.log('Download response status:', response.status);
      
      if (response.ok) {
        const blob = await response.blob();
        console.log('Downloaded blob size:', blob.size, 'type:', blob.type);
        
        if (blob.size === 0) {
          alert('The document appears to be empty. Please contact your administrator.');
          return;
        }
        
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = fileName || 'medical_document.pdf';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
        
        console.log('Document downloaded successfully');
      } else {
        let errorMessage = 'Failed to download document.';
        
        try {
          const errorData = await response.json();
          errorMessage = errorData.error || errorMessage;
        } catch (e) {
          try {
            const errorText = await response.text();
            if (errorText) {
              errorMessage = errorText;
            }
          } catch (e2) {
            // Use default error message
          }
        }
        
        console.error('Download failed:', response.status, errorMessage);
        
        if (response.status === 404) {
          alert('Document not found on server. It may have been moved or deleted.');
        } else if (response.status === 403) {
          alert('Access denied. You do not have permission to download this document.');
        } else if (response.status === 503) {
          alert('Document download service is not available in development mode.');
        } else {
          alert(`Failed to download document: ${errorMessage}`);
        }
      }
    } catch (error) {
      console.error('Error downloading document:', error);
      alert('Error downloading document. Please check your connection and try again.');
    }
  };

  const fetchEmployeeHistoryData = async (employeeEmail, employeeName) => {
    try {
      setHistoryLoading(true);
      
      const backendUrl = getBackendUrl();
      const response = await fetch(`${backendUrl}/api/leave-history/user-requests`, {
        method: 'GET',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
        },
      });
      
      if (response.ok) {
        const allRequests = await response.json();
        
        const employeeRequests = allRequests.filter(req => 
          req.employeeEmail === employeeEmail || req.employee_email === employeeEmail
        );
        
        const currentYear = new Date().getFullYear();
        const thisYearRequests = employeeRequests.filter(req => {
          if (req.createdAt) {
            const requestYear = new Date(req.createdAt).getFullYear();
            return requestYear === currentYear;
          }
          return false;
        });
        
        const approvedThisYear = thisYearRequests.filter(req => 
          req.status && req.status.toLowerCase() === 'approved'
        );
        
        const lastApproval = approvedThisYear
          .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))[0];
        
        return {
          previousLeavesThisYear: approvedThisYear.length,
          lastApprovalDate: lastApproval ? formatDateForDisplay(lastApproval.dateApplied || lastApproval.createdAt) : 'No previous approvals',
          totalRequestsThisYear: thisYearRequests.length,
          recentRequests: employeeRequests.slice(0, 5)
        };
      }
      
      return {
        previousLeavesThisYear: 0,
        lastApprovalDate: 'No previous approvals',
        totalRequestsThisYear: 0,
        recentRequests: []
      };
      
    } catch (error) {
      console.error('Error fetching employee history:', error);
      return {
        previousLeavesThisYear: 0,
        lastApprovalDate: 'Unable to fetch',
        totalRequestsThisYear: 0,
        recentRequests: []
      };
    } finally {
      setHistoryLoading(false);
    }
  };

  // ✅ FIXED: Clean fetchEmployeeBalance function with LOP support and fallback lookup
  const fetchEmployeeBalance = async (employeeId, employeeEmail, requestDays = 0, requestId = null) => {
    console.log('🚀 ========== FETCHING BALANCE ==========');
    console.log('🔍 Input:', { employeeId, employeeEmail, requestDays });
    
    try {
      // Fallback: If no employee ID, try getting from employee list
      if (!employeeId) {
        console.warn('⚠️ No employee ID provided, falling back to employee list lookup');
        
        if (!employeeEmail) {
          console.warn('⚠️ No employee email provided for fallback lookup');
          console.log('🏁 ========== END FETCHING BALANCE ==========\n');
          return 'Casual: --/12 | Medical: --/15';
        }
        
        const backendUrl = getBackendUrl();
        const listResponse = await fetch(`${backendUrl}/api/employee-list`, {
          method: 'GET',
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json',
          },
        });
        
        if (listResponse.ok) {
          const employees = await listResponse.json();
          const employee = employees.find(emp => 
            emp.email && emp.email.toLowerCase() === employeeEmail.toLowerCase()
          );
          
          if (employee && employee.id) {
            console.log('✅ Found employee in list:', { name: employee.name, id: employee.id });
            employeeId = employee.id;
          } else {
            console.warn('⚠️ Employee not found in list:', employeeEmail);
            console.log('🏁 ========== END FETCHING BALANCE ==========\n');
            return 'Casual: --/12 | Medical: --/15';
          }
        } else {
          console.error('❌ Failed to fetch employee list for fallback lookup');
          console.log('🏁 ========== END FETCHING BALANCE ==========\n');
          return 'Casual: --/12 | Medical: --/15';
        }
      }
      
      // Main API call to get balance
     // ✅ NEW: Build URL with request_id parameter if provided
const backendUrl = getBackendUrl();
let url = `${backendUrl}/api/employee/${employeeId}/leave-balance`;

if (requestId) {
  url += `?request_id=${requestId}`;
  console.log('🔍 Checking LOP for specific request:', requestId);
}

console.log('🌐 REQUEST URL:', url);
      
      const response = await fetch(url, {
        method: 'GET',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        }
      });
      
      console.log('📡 Response status:', response.status);
      
      if (response.ok) {
        const rawText = await response.text();
        console.log('📄 RAW RESPONSE TEXT:', rawText);
        
        let data;
        try {
          data = JSON.parse(rawText);
          console.log('✅ PARSED JSON:', data);
        } catch (parseError) {
          console.error('❌ JSON PARSE ERROR:', parseError);
          console.log('🏁 ========== END FETCHING BALANCE ==========\n');
          return 'Casual: --/12 | Medical: --/15';
        }
        
        const balances = data.balances || data || {};
        console.log('📦 Balances object:', balances);
        
        const formatNumber = (num) => {
          if (num === null || num === undefined || isNaN(num)) return '0';
          return Number(num).toFixed(1);
        };
        
        // Extract casual leave with LOP support
        const casualData = balances.casual || balances.CASUAL || balances.Casual || {};
        
        // Get the annual allocated amount (should be 12)
        const casualAllocated = parseFloat(casualData.allocated || 12);
        
        // Get current available balance (can be negative for LOP)
        const casualCarryForward = parseFloat(
          casualData.carry_forward_from_previous_month || 
          casualData.carry_forward || 
          casualData.carryForward ||
          0
        );
        
        const casualUsed = parseFloat(casualData.used || 0);
        const casualPending = parseFloat(casualData.pending || 0); // ✅ Get pending requests
        
        const casualRemaining = parseFloat(
          casualData.remaining !== undefined ? casualData.remaining :
          casualData.available !== undefined ? casualData.available :
          casualCarryForward
        );
        
        const casualLopDays = parseFloat(casualData.lop_days || casualData.lopDays || 0);
        
        // ✅ FINAL FIX: Backend's 'remaining' is calculated WITHOUT any pending requests
        // remaining = allocated + extra - used (does NOT include pending)
        // So we need to check if approving THIS request would cause LOP
        
        const totalPending = parseFloat(casualPending);
        const otherPendingRequests = Math.max(0, totalPending - requestDays); // Other pending requests excluding this one
        
        // Check LOP status
        const isCurrentlyInLop = casualRemaining < 0; // Already negative
        
        // This request causes LOP if:
        // remaining - this request - other pending < 0
        const balanceAfterAllRequests = casualRemaining - requestDays - otherPendingRequests;
        const willThisRequestCauseLop = !isCurrentlyInLop && balanceAfterAllRequests < 0;
        
        const casualIsLop = Boolean(casualData.is_lop || casualData.isLop) || isCurrentlyInLop || willThisRequestCauseLop;
        
        console.log('💰 Casual parsed:', { 
          allocated: casualAllocated,
          carryForward: casualCarryForward,
          used: casualUsed,
          pending: casualPending,
          remaining: casualRemaining,
          requestDays: requestDays,
          totalPending: totalPending,
          otherPendingRequests: otherPendingRequests,
          balanceAfterAllRequests: balanceAfterAllRequests,
          isCurrentlyInLop: isCurrentlyInLop,
          willThisRequestCauseLop: willThisRequestCauseLop,
          lopDays: casualLopDays,
          isLop: casualIsLop
        });
        
        // Extract medical leave
        const medicalData = balances.medical || balances.SICK || balances.sick || balances.Medical || {};
        
        const medicalAllocated = parseFloat(medicalData.allocated || 15);
        const medicalUsed = parseFloat(medicalData.used || 0);
        const medicalRemaining = parseFloat(
          medicalData.remaining !== undefined ? medicalData.remaining :
          medicalData.available !== undefined ? medicalData.available :
          (medicalAllocated - medicalUsed)
        );
        
        console.log('🥼 Medical parsed:', { 
          allocated: medicalAllocated,
          used: medicalUsed, 
          remaining: medicalRemaining
        });
        
        // Format display with Used days and LOP indicator
        const casualUsedDisplay = formatNumber(casualUsed);
        
        // Calculate what remains from the annual allocation after subtracting used
        // If there's LOP, the "remaining" might be negative, but we want to show how many days are left from the 12
        let casualRemainingFromAnnual;
        if (casualRemaining < 0) {
          // In LOP: show annual - used (ignoring the negative remaining)
          casualRemainingFromAnnual = casualAllocated - casualUsed;
        } else {
          // Not in LOP: show actual remaining
          casualRemainingFromAnnual = casualRemaining;
        }
        
        const casualDisplay = formatNumber(Math.max(0, casualRemainingFromAnnual));
        const casualTotalDisplay = formatNumber(casualAllocated);  // Use allocated (12)
        
        const medicalDisplay = formatNumber(medicalRemaining);
        const medicalAllocDisplay = formatNumber(medicalAllocated);
        
        // Build casual text: Used: X Casual: Y/Z [LOP: W]
        let casualText = `Used: ${casualUsedDisplay} Casual: ${casualDisplay}/${casualTotalDisplay}`;
        
        // ✅ Show LOP based on whether THIS specific request causes it
        if (isCurrentlyInLop) {
          // Already in LOP (negative balance)
          casualText += ` [LOP: ${formatNumber(Math.abs(casualRemaining))}]`;
        } else if (willThisRequestCauseLop) {
          // THIS specific request will cause NEW LOP
          // Show how much LOP this would create
          const lopAmount = Math.abs(balanceAfterAllRequests);
          casualText += ` [PENDING LOP: ${formatNumber(lopAmount)}]`;
        } else if (casualLopDays > 0) {
          // Has LOP from backend
          casualText += ` [LOP: ${formatNumber(casualLopDays)}]`;
        }
        
        const result = `${casualText} | Medical: ${medicalDisplay}/${medicalAllocDisplay}`;

        console.log('✅ FINAL RESULT:', result);
        console.log('🏁 ========== END FETCHING BALANCE ==========\n');
        
        return result;
        
      } else {
        console.error('❌ API ERROR - Status:', response.status);
        const errorText = await response.text();
        console.error('❌ Error response body:', errorText);
      }
      
    } catch (error) {
      console.error('❌ EXCEPTION:', error);
      console.error('❌ Error stack:', error.stack);
    }
    
    console.log('📌 Using fallback');
    console.log('🏁 ========== END FETCHING BALANCE ==========\n');
    return 'Casual: --/12 | Medical: --/15';
  };

  // ✅ Helper function to refresh all balances
  const refreshAllBalances = async () => {
    console.log('🔄 Refreshing all employee balances...');
    
    const updatedData = await Promise.all(
      approvalsData.map(async (item) => {
        const employeeId = item.originalData?.employeeId || 
                          item.originalData?.employee_id || 
                          item.originalData?.userId || 
                          item.originalData?.user_id;
        const employeeEmail = item.email;
        
        // ✅ Get request days for this specific request
        const requestDays = parseFloat(item.originalData?.daysRequested || item.originalData?.days_requested || 0);
        
        const updatedBalance = await fetchEmployeeBalance(employeeId, employeeEmail, requestDays, item.id);
        
        return {
          ...item,
          balance: updatedBalance
        };
      })
    );
    
    setApprovalsData(updatedData);
    console.log('✅ All balances refreshed');
  };

  const fetchManagerInfo = async () => {
    try {
      const backendUrl = getBackendUrl();
      const response = await fetch(`${backendUrl}/api/user`, {
        method: 'GET',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
        },
      });

      if (response.ok) {
        const userData = await response.json();
        console.log('✅ Manager info fetched:', userData);
        setManagerInfo({
          managerId: userData.id,
          teamSize: userData.team_size || 0,
          canApproveLeaves: userData.can_approve_leaves || false
        });
      }
    } catch (error) {
      console.error('Error fetching manager info:', error);
    }
  };

  const fetchPendingApprovals = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const backendUrl = getBackendUrl();
      
      console.log('🔍 Fetching pending approvals from:', `${backendUrl}/api/pending-approvals`);
      
      const response = await fetch(`${backendUrl}/api/pending-approvals`, {
        method: 'GET',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
        },
      });

      console.log('📡 Response status:', response.status);

      if (!response.ok) {
        throw new Error(`Failed to fetch pending approvals: ${response.status}`);
      }

      const data = await response.json();
      
      if (!Array.isArray(data)) {
        throw new Error('Invalid response format - expected array');
      }
      
      console.log('📦 Raw pending approvals data:', data);
      console.log(`📊 Total requests received: ${data.length}`);
      
      const pendingRequests = data;
      
      console.log(`✅ Showing ${pendingRequests.length} pending requests`);
      
      const formattedDataPromises = pendingRequests.map(async (request) => {
        const employeeEmail = request.employeeEmail || request.employee_email || '';
        const employeeName = request.employeeName || request.employee_name || 'Unknown Employee';
        const employeeId = request.employeeId || request.employee_id || request.userId || request.user_id;
        
        console.log('🔍 Processing request:', {
          requestId: request.id,
          employeeId: employeeId,
          employeeEmail: employeeEmail,
          employeeName: employeeName
        });
        
        // ✅ Get profile picture (will return set picture or default, no random)
        const profilePicture = await getUserProfilePicture(employeeEmail, employeeName);
        
        // ✅ Get the days requested for THIS specific request
        const requestDays = parseFloat(request.daysRequested || request.days_requested || 0);
        
        // ✅ Pass request days to check if THIS request will cause LOP
        const employeeBalance = await fetchEmployeeBalance(
  employeeId, 
  employeeEmail, 
  requestDays,
  request.id  // ✅ Pass the request ID
);
        
        console.log('✅ Balance fetched:', {
          requestId: request.id,
          employeeId: employeeId,
          balance: employeeBalance
        });
        
        const documentInfo = getMedicalDocumentInfo(request);
        console.log('Document info for request', request.id, ':', documentInfo);
        
        return {
          id: request.id,
          Image: profilePicture,
          name: employeeName,
          email: employeeEmail,
          type: formatLeaveType(request.leaveType || request.leave_type),
          duration: (() => {
            const days = request.daysRequested || request.days_requested || 1;
            const isHalfDay = request.is_half_day || request.isHalfDay || false;
            if (isHalfDay && days === 0.5) return "Half Day (0.5)";
            if (days === 1) return "1 day";
            if (days % 1 === 0) return `${days} days`;
            return `${days} days`;
          })(),
          dates: request.dateRange || 
                 (request.startDate && request.endDate ? 
                   (request.startDate === request.endDate ? 
                     formatDateForDisplay(request.startDate) : 
                     `${formatDateForDisplay(request.startDate)} - ${formatDateForDisplay(request.endDate)}`) : 
                   'Not specified'),
          balance: employeeBalance,
          reason: request.reason || 'No reason provided',
          certificate: documentInfo.hasDocument,
          certificateUrl: documentInfo.downloadUrl,
          certificateFileName: documentInfo.fileName,
          priority: determinePriority(request),
          prevLeaves: null,
          lastApprovalDate: null,
          date: formatDateForDisplay(request.dateApplied || request.createdAt),
          originalData: request,
          isHalfDay: request.is_half_day || request.isHalfDay || false,
          halfDayPeriod: request.half_day_period || request.halfDayPeriod || null,
          assignedManagerId: request.assigned_manager_id,
          requestingManagerId: request.requesting_manager_id
        };
      });
      
      const formattedData = await Promise.all(formattedDataPromises);
      console.log('✅ Formatted pending data:', formattedData);
      setApprovalsData(formattedData);
      
    } catch (err) {
      console.error('❌ Error fetching pending approvals:', err);
      setError(err.message || 'Failed to load pending approvals');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchManagerInfo();
    fetchPendingApprovals();
    
    // Add event listener for balance updates
    const handleBalanceUpdate = () => {
      console.log('📢 Balance update event detected - refreshing approvals');
      fetchPendingApprovals();
    };
    
    window.addEventListener('leaveBalanceAdjusted', handleBalanceUpdate);
    window.addEventListener('globalConfigChanged', handleBalanceUpdate);
    
    return () => {
      window.removeEventListener('leaveBalanceAdjusted', handleBalanceUpdate);
      window.removeEventListener('globalConfigChanged', handleBalanceUpdate);
    };
  }, []);

    const handleApproveReject = async (action, requestId, comments = '') => {
      try {
        setIsProcessing(true);
        
        const backendUrl = getBackendUrl();
        const response = await fetch(`${backendUrl}/api/approve-leave/${requestId}`, {
          method: 'POST',
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json',
          },
          body: JSON.stringify({
            action: action,
            comments: comments
          })
        });
  
        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          throw new Error(errorData.error || `Failed to ${action} leave request`);
        }
  
        const responseData = await response.json();
        const lopWarning = responseData.warning || '';
  
        setApprovalsData(prevData => prevData.filter(item => item.id !== requestId));
        
        const event = new CustomEvent(`leaveRequest${action === 'approve' ? 'Approved' : 'Rejected'}`, {
          detail: {
            requestId: requestId,
            action: action,
            timestamp: Date.now()
          }
        });
        window.dispatchEvent(event);
        
        const updateEvent = new CustomEvent('leaveRequestUpdated', {
          detail: {
            requestId: requestId,
            action: action,
            timestamp: Date.now()
          }
        });
        window.dispatchEvent(updateEvent);
        
        setTimeout(() => {
          fetchPendingApprovals();
        }, 1000);
        
        let successMessage = `Leave request ${action}ed successfully!`;
        
        if (lopWarning) {
          successMessage += `\n\n${lopWarning}`;
        }
        
        alert(successMessage);
        
      } catch (error) {
        console.error(`Error ${action}ing leave request:`, error);
        
        let errorMessage = error.message;
        if (errorMessage.includes('Insufficient casual leave balance') || 
            errorMessage.includes('insufficient balance')) {
          errorMessage = 'Unable to process leave request. Please try again.';
        }
        
        alert(`Failed to ${action} leave request: ${errorMessage}`);
      } finally {
        setIsProcessing(false);
      }
    };
  
    const handleRejectClick = (item) => {
      setSelectedEmployee(item);
      setIsModalOpen(true);
    };
  
    const handleReject = async () => {
      if (!comment.trim()) {
        alert('Please provide a reason for rejection');
        return;
      }
      
      await handleApproveReject('reject', selectedEmployee.id, comment.trim());
      setComment('');
      setIsModalOpen(false);
      setSelectedEmployee(null);
    };
  
    const handleApprove = async (item) => {
      const employeeId = item.originalData?.employeeId || 
                        item.originalData?.employee_id || 
                        item.originalData?.userId || 
                        item.originalData?.user_id;
      
      // let lopWarning = '';
      // const isCasualLeave = (item.type || '').toLowerCase().includes('casual');
      
      // if (isCasualLeave && employeeId) {
      //   try {
      //     const backendUrl = getBackendUrl();
      //     const response = await fetch(`${backendUrl}/api/employee/${employeeId}/leave-balance`, {
      //       method: 'GET',
      //       credentials: 'include',
      //       headers: {
      //         'Content-Type': 'application/json',
      //       }
      //     });
          
      //     if (response.ok) {
      //       const data = await response.json();
      //       const casualRemaining = parseFloat(data.balances?.casual?.remaining || 0);
      //       const daysRequested = parseFloat(item.originalData?.days_requested || 1);
            
      //       const balanceAfterApproval = casualRemaining - daysRequested;
            
      //       if (balanceAfterApproval < 0) {
      //         const lopDays = Math.abs(balanceAfterApproval);
      //         lopWarning = `\n\n⚠️ WARNING: This approval will result in Loss of Pay (LOP)\n` +
      //                     `Current available: ${casualRemaining.toFixed(1)} days\n` +
      //                     `Requested: ${daysRequested.toFixed(1)} days\n` +
      //                     `Result: ${balanceAfterApproval.toFixed(1)} days\n` +
      //                     `LOP amount: ${lopDays.toFixed(1)} days\n\n` +
      //                     `${lopDays.toFixed(1)} days will be deducted from salary.`;
      //       }
      //     }
      //   } catch (error) {
      //     console.error('Error checking LOP status:', error);
      //   }
      // }
      
      // const confirmMessage = `Are you sure you want to approve ${item.name}'s ${item.type} request for ${item.duration}?${lopWarning}`;
      const confirmMessage = `Are you sure you want to approve ${item.name}'s ${item.type} request for ${item.duration}?`;
      const confirmApprove = window.confirm(confirmMessage);
      
      if (confirmApprove) {
        await handleApproveReject('approve', item.id, 'Approved by manager');
      }
    };

  const handleCancel = () => {
    setComment('');
    setIsModalOpen(false);
    setSelectedEmployee(null);
  };

  const handleClose = () => {
    setComment('');
    setIsModalOpen(false);
    setSelectedEmployee(null);
  };

  const filteredApprovals = approvalsData.filter(item => {
    const matchesType =
      filterType === 'All Types' || item.type.toLowerCase().includes(filterType.toLowerCase());
    const matchesSearch =
      item.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.reason.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesType && matchesSearch;
  });

  if (error) {
    return (
      <div className="approvals-container">
        <div className="first">
          <h2>Team Pending Approvals</h2>
          <p>Error loading team pending approvals</p>
        </div>
        <div style={{ 
          display: 'flex', 
          justifyContent: 'center', 
          alignItems: 'center', 
          height: '200px',
          flexDirection: 'column',
          gap: '16px',
          color: '#dc2626'
        }}>
          <p>{error}</p>
          <button 
            onClick={fetchPendingApprovals}
            style={{
              padding: '8px 16px',
              backgroundColor: '#3b82f6',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer'
            }}
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="approval-main">
      <div className="mdmanager-navbar" style={{marginTop:'1px', overflowX:'hidden'}}><Navbar1/></div>
      <div className="manager-approval-mainbar" style={{width:'100%', overflowX:'hidden'}}>
        <div className="pamainbar" style={{ width:'100%', overflowX:'hidden'}}><Mainbar/></div>
        <div className="approvals-container" style={{width:'100%', overflowX:'hidden', maxWidth:'100%'}}>
          <div className="first">
            <h2 style={{color:'#2196F3'}}>Team Pending Approvals</h2>
            
            {/* {managerInfo.teamSize > 0 && (
              <div style={{
                backgroundColor: '#e0f2fe',
                border: '1px solid #0277bd',
                borderRadius: '6px',
                padding: '12px 16px',
                marginTop: '12px',
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}>
                <span style={{ fontSize: '16px' }}>👥</span>
                <span style={{ fontSize: '14px', color: '#0277bd' }}>
                  <strong>Team Management:</strong> You are managing {managerInfo.teamSize} team member{managerInfo.teamSize !== 1 ? 's' : ''}.
                </span>
              </div>
            )} */}

            <div className="top-controls">
              <input
                type="text"
                className="search-bar-manag-team"
                placeholder="Search for..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />

              <select
                className="filter-select"
                value={filterType}
                onChange={(e) => setFilterType(e.target.value)}
              >
                <option>All Types</option>
                <option>Casual</option>
                <option>Medical</option>
                <option>Permission</option>
              </select>
              
              <button className='approval-refresh-btn'
                onClick={fetchPendingApprovals}
                style={{
                  padding: '8px 5px',
                  backgroundColor: '#2196F3',
                  color:'#ffffff',
                  border: '1px solid #d1d5db',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  // width: '105px',
                  height:'42px',
                }}
                disabled={loading}
              >
                {/* <img src="/Images/Refresh.png" alt="@" /> Refresh */}
              </button>
            </div>
          </div>

          {filteredApprovals.length === 0 ? (
            <div style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              height: '200px',
              flexDirection: 'column',
              gap: '16px',
              color: '#6b7280'
            }}>
              <p>No pending approval requests found from your team members</p>
              <p style={{ fontSize: '14px', opacity: '0.8' }}>
                {searchTerm || filterType !== 'All Types' ? 
                  'Try adjusting your search or filters' : 
                  managerInfo.teamSize === 0 ?
                    'No team members assigned to you yet. Contact HR to assign team members.' :
                    'All team requests have been processed or no requests exist'
                }
              </p>
            </div>
          ) : (
            filteredApprovals.map((item, index) => (
              <div key={`${item.id}-${index}`} className="approval-card">
                <div className="approval-header">
                  <div className="profile-section" style={{display:'flex'}}>
                    <div className="profile-section1">
                      {item.Image && (
                        <img 
                          src={item.Image} 
                          alt="Profile" 
                          className="profile-image-whatsapp" 
                          style={{
                            width: '40px',
                            height: '40px',
                            borderRadius: '50%',
                            objectFit: 'cover',
                            border: '2px solid #e5e7eb',
                            marginBottom: '8px'
                          }}
                          onError={(e) => {
                            // ✅ If image fails to load, use default
                            console.log('❌ Image failed to load, using default');
                            e.target.src = DEFAULT_PROFILE_PICTURE;
                          }}
                        />
                      )}
                      <div className="profile-section2" style={{marginTop:'-45px',marginLeft:'5px'}}>
                        <div className="user-info">
                          <div className="user-name" style={{
                            fontSize: '14px',
                            fontWeight: '600',
                            color: '#374151',
                            marginBottom: '2px'
                          }}>
                            {item.name}
                          </div>
                          <div className="user-email" style={{
                            fontSize: '12px',
                            color: '#6b7280',
                          }}>
                            {item.email}
                          </div>
                          <div style={{
                            fontSize: '11px',
                            color: '#059669',
                            // backgroundColor: '#dcfce7',
                            padding: '2px 6px',
                            borderRadius: '8px',
                            marginTop: '4px',
                            display: 'inline-block'
                          }}>
                            Team Member
                          </div>
                        </div>
                      </div>    
                    </div>
                  </div>
                  <div className={`priority ${item.priority}`}>{item.priority} priority</div>
                </div>
                
                <div className="section_first">
                  <div className="section section-first-one"  > <span style={{fontWeight:'700',textTransform:'capitalize'}}> Leave Type </span><div> {item.type} </div></div>
                  <div className="section">
                    <span style={{fontWeight:'700',textTransform:'capitalize'}}> Duration </span>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      {item.duration}
                      {item.isHalfDay && (
                        <>
                          <span style={{
                            backgroundColor: '#fef3c7',
                            color: '#d97706',
                            padding: '2px 8px',
                            borderRadius: '12px',
                            fontSize: '11px',
                            fontWeight: '500'
                          }}>Half Day</span>
                          {item.halfDayPeriod && (
                            <span style={{
                              backgroundColor: '#e0f2fe',
                              color: '#0369a1',
                              padding: '2px 8px',
                              borderRadius: '12px',
                              fontSize: '11px',
                              fontWeight: '500'
                            }}>
                              {item.halfDayPeriod === 'morning' ? '🌅 Morning' : '🌆 Afternoon'}
                            </span>
                          )}
                        </>
                      )}
                    </div>
                  </div>
                </div>
                
                <div className="section_two">
                  <div className="section section-Two-one"> <span style={{fontWeight:'700',textTransform:'capitalize'}}> Dates </span> <div>{item.dates}</div></div>
                  <div className="section current-balance">
                    <span style={{fontWeight:'700',textTransform:'capitalize'}}> Current Balance </span>
                    <div style={{ 
                      display: 'flex', 
                      alignItems: 'center',
                      gap: '8px'
                    }}>
                      <span>{item.balance}</span>
                      {item.balance && (item.balance.includes('[LOP:') || item.balance.includes('[PENDING LOP:')) && (
                        <span style={{
                          backgroundColor: item.balance.includes('[PENDING LOP:') ? '#f59e0b' : '#dc2626',
                          color: 'white',
                          fontSize: '10px',
                          padding: '2px 6px',
                          borderRadius: '8px',
                          fontWeight: '600'
                        }}>
                          {item.balance.includes('[PENDING LOP:') ? 'Pending LOP' : 'Loss of Pay'}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
                
                <div className="section"> <span style={{fontWeight:'700',textTransform:'capitalize'}}> Reason </span> <div>{item.reason}</div></div>

                {(item.type === 'Medical Leave' || item.type.toLowerCase().includes('medical')) && (
                  <div className="section">
                    <strong>Medical Certificate</strong>
                    <div style={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      gap: '8px',
                      marginTop: '4px'
                    }}>
                      {item.certificate ? (
                        <>
                          <span style={{ 
                            color: '#059669', 
                            backgroundColor: '#dcfce7',
                            padding: '4px 8px',
                            borderRadius: '12px',
                            fontSize: '12px',
                            fontWeight: '500',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '4px'
                          }}>
                            ✓ Document uploaded
                          </span>
                          {item.certificateUrl && (
                            <button
                              onClick={() => handleDocumentDownload(item.certificateUrl, item.certificateFileName, item.originalData)}
                              style={{
                                background: '#3b82f6',
                                color: 'white',
                                border: 'none',
                                borderRadius: '4px',
                                padding: '4px 8px',
                                fontSize: '12px',
                                cursor: 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '4px'
                              }}
                              title="Download medical document"
                            >
                              📄 View Document
                            </button>
                          )}
                        </>
                      ) : (
                        <span style={{ 
                          color: '#f59e0b', 
                          backgroundColor: '#fef3c7',
                          padding: '4px 8px',
                          borderRadius: '12px',
                          fontSize: '12px',
                          fontWeight: '500'
                        }}>
                          ⚠ No document uploaded
                        </span>
                      )}
                    </div>
                    {item.certificateFileName && (
                      <div style={{ 
                        fontSize: '11px', 
                        color: '#6b7280', 
                        marginTop: '4px',
                        fontStyle: 'italic'
                      }}>
                        File: {item.certificateFileName}
                      </div>
                    )}
                  </div>
                )}

                <div className="action-buttons" style={{ marginTop: '16px' }}>

                  <button 
                    className="approve"
                    onClick={() => handleApprove(item)}
                    disabled={isProcessing}
                    style={{
                      fontSize: '10px',
                      opacity: isProcessing ? 0.6 : 1,
                      cursor: isProcessing ? 'not-allowed' : 'pointer'
                    }}
                  >
                    {isProcessing ? 'Processing...' : 'Approve'}
                  </button>
                  <button 
                    className="reject" 
                    onClick={() => handleRejectClick(item)}
                    disabled={isProcessing}
                    style={{ 
                      fontSize: '10px',
                      opacity: isProcessing ? 0.6 : 1,
                      cursor: isProcessing ? 'not-allowed' : 'pointer'
                    }}
                  >
                    {isProcessing ? 'Processing...' : 'Reject'}
                  </button>
                  
                </div>

                <div className="date-label">{item.date}</div>
              </div>
            ))
          )}

          {isModalOpen && selectedEmployee && (
            <div className="modal-overlay">
              <div className="modal-container">
                <div className="modal-header">
                  <h2>Add Comment</h2>
                  <button className="close-button" onClick={handleClose}>×</button>
                </div>
                <div className="modal-content">
                  <div style={{ marginBottom: '16px', padding: '12px', backgroundColor: '#f9fafb', borderRadius: '4px' }}>
                    <strong>Rejecting request from team member:</strong> {selectedEmployee.name}<br />
                    <strong>Email:</strong> {selectedEmployee.email}<br />
                    <strong>Leave Type:</strong> {selectedEmployee.type}<br />
                    <strong>Duration:</strong> {selectedEmployee.duration}
                    {selectedEmployee.originalData?.is_half_day && (
                      <span style={{ marginLeft: '8px', backgroundColor: '#fef3c7', color: '#d97706', padding: '2px 6px', borderRadius: '8px', fontSize: '11px', fontWeight: '500' }}>Half Day</span>
                    )}
                    <br />
                    <strong>Dates:</strong> {selectedEmployee.dates}
                  </div>
                  <div className="form-group">
                    <label>Reason for rejection *</label>
                    <textarea
                      value={comment}
                      onChange={(e) => setComment(e.target.value)}
                      placeholder="Please provide a detailed reason for rejecting this team member's request..."
                      rows={5}
                      maxLength={500}
                      style={{ width: '100%', resize: 'vertical' }}
                    />
                    <small style={{ color: '#6b7280', fontSize: '12px' }}>
                      {comment.length}/500 characters
                    </small>
                  </div>
                  <div className="modal-actions">
                    <button 
                      className="reject-request-btn" 
                      onClick={handleReject}
                      disabled={!comment.trim() || isProcessing}
                      style={{ 
                        opacity: (!comment.trim() || isProcessing) ? 0.6 : 1,
                        cursor: (!comment.trim() || isProcessing) ? 'not-allowed' : 'pointer'
                      }}
                    >
                      {isProcessing ? 'Rejecting...' : 'Reject Request'}
                    </button>
                    <button 
                      className="cancel-btn" 
                      onClick={handleCancel}
                      disabled={isProcessing}
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}
          
          <style jsx>{`
            @keyframes spin {
              0% { transform: rotate(0deg); }
              100% { transform: rotate(360deg); }
            }
            
            .profile-section {
              display: flex;
              flex-direction: column;
              // align-items: center;
              text-align: center;
            }
            
            .profile-image-whatsapp {
              display: block;
            }
            
            .user-info {
              display: flex;
              flex-direction: column;
              align-items: center;
            }
            
            .user-name {
              font-weight: 600;
              color: #374151;
            }
            
            .user-email {
              color: #6b7280;
              font-size: 0.75rem;
            }
            
            .action-buttons {
              display: flex;
              gap: 10px;
              justify-content: flex-end;
            }
          `}</style>
        </div>
      </div>
    </div>  
  );
};

export default Approvals;